import { styled } from '@mui/material/styles';
import { lazy, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { Theme, useTheme } from '@mui/material';
import { useAppSelector } from '../../common/hooks';
import { Footer } from '../../components/molecules';
import { AlertDialog } from '../../components/molecules/AlertDialog/AlertDialog';
import { toggleSessionExpiredModal } from '../../features/auth/authSlice';
import { getCurrentYear } from '../../utils';
import LanguageSelectorComponent from '../../components/organisms/LanguageSelector';

const OktaLoginWidget = lazy(() => import('../../features/login-okta-widget'));

const StyledCarouselWrapper = styled('section')(() => ({
    marginBottom: '40px',
    marginTop: 'auto'
}));

const PageContainer = styled('div')(({ theme }: { theme: Theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    minHeight: '100vh',
    backgroundColor: theme.palette.grey[100],
    position: 'relative'
}));

const MainContent = styled('main')(() => ({
    display: 'grid',
    gridTemplateColumns: '100%',
    placeItems: 'center',
    marginTop: '100px',
    flex: 1,
    '@media (max-width: 1440px)': {
        marginTop: '20px'
    },
    '@media (min-width: 1440px)': {
        marginTop: '20px',
        '#okta-sign-in': {
            marginTop: 0
        }
    },
    '@media (min-width: 1230px)': {
        '.auth-container': {
            maxHeight: 650
        }
    }
}));

const RightContainer: any = styled('section')(() => ({
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
    justifyContent: 'space-evenly',
    padding: '0 10%',
    '@media (max-width: 1440px)': {
        padding: '0 5%'
    }
}));

const StyledGoeDigitalAdvisorLogo = styled('img')({
    width: '83%',
    height: 'auto',
    marginBottom: '40px'
});

type DropdownNames = 'language';

const LoginContainer = () => {
    const { t } = useTranslation();
    const theme = useTheme();
    const dispatch = useDispatch();
    const showSessionExpiredModal = useAppSelector((state) => state.auth.showSessionExpiredModal);
    const currentLanguage = useAppSelector((state) => state.global.currentLanguage);
    const [showModal, setShowModal] = useState(showSessionExpiredModal);
    const [activeDropdown, setActiveDropdown] = useState<DropdownNames>();

    const onToggleDropdown = (name: DropdownNames) =>
        setActiveDropdown((oldName) => (oldName === name ? undefined : name));
    // Clear logout flag when login page loads
    useEffect(() => {
        sessionStorage.removeItem('isLoggingOut');
        console.log('LoginContainer: Cleared isLoggingOut flag');
    }, []);

    // Sync i18n with Redux language state
    // useEffect(() => {
    //     if (currentLanguage && i18n.language !== currentLanguage) {
    //         i18n.changeLanguage(currentLanguage);
    //     }
    // }, [currentLanguage, i18n]);

    // const handleLanguageChange = (event: any) => {
    //     const newLanguage = event.target.value;
    //     // Save to localStorage (also handled by i18n.on('languageChanged') in i18n.ts)
    //     localStorage.setItem('userLanguage', newLanguage);
    //     dispatch(updateLanguage(newLanguage));
    //     i18n.changeLanguage(newLanguage);
    // };

    const handleCloseModal = () => {
        dispatch(toggleSessionExpiredModal(false));
        setShowModal(false);
    };

    const advIncludedFooterLinks = [
        {
            label: t('UPDATED_TERMS_OF_USE_AGREEMENT'),
            link: `${process.env.PUBLIC_URL}/assets/pdf/en/Digital_Advisor_Terms_of_Use.pdf`
        },
        {
            label: t('PRIVACY_NOTICE_TEXT'),
            link: 'https://www.franklintempletonglobal.com/privacy'
        },
        {
            label: t('ADV_TEXT'),
            link: `https://www.franklintempleton.com/help/client-relationship-summaries-for-retail-investors`
        }
    ];

    return (
        <PageContainer theme={theme}>
            <LanguageSelectorComponent
                isDropdownActive={activeDropdown === 'language'}
                onToggleDropdown={() => onToggleDropdown('language')}
                isLogInContainer={true}
            />
            <MainContent>
                <RightContainer item xs={12} sm={12} md={5} lg={5}>
                    <StyledGoeDigitalAdvisorLogo
                        src={`${process.env.PUBLIC_URL}/assets/images/GOE_Digital_Advisor_blue_logo.svg`}
                        alt={t('TEXT_LOGIN_SCREEN_WELCOME_TEXT_2')}
                    />
                    <OktaLoginWidget currentLanguage={currentLanguage || 'en'} />
                </RightContainer>
            </MainContent>

            <StyledCarouselWrapper>
                {/* AEX-2063 Based on compliance feedback, 
                    the login page awards carousal needs to be deprecated for August release.
                    <ImageCarousel /> 
                */}
            </StyledCarouselWrapper>

            <Footer
                primaryText={`${t('DESCRIPTION_FOOTER_THREE', { currentYear: getCurrentYear() })}`}
                links={advIncludedFooterLinks}
                eulaVariant={false} // to hide the detailed footer
            />

            <AlertDialog
                handleClose={handleCloseModal}
                open={showModal}
                content={`${t('SESSION_EXPIRED_MODAL_CONTENT')}`}
                title={t('SESSION_EXPIRED_MODAL_TITLE')}
                aria-label="close-session-expired-popup"
                data-testid="session-expired-modal-close-button"
            />
        </PageContainer>
    );
};

export default LoginContainer;
